import React, { useMemo } from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { GridItem, HStack, Icon, Popover, PopoverContent, PopoverTrigger, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IconButtonMenu from '../../../../../../components/IconButtonMenu';
import StackedButtonGroup from '../../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import Row from '../../../../../../components/Row';
import RowGridColumn from '../../../../../../components/Row/components/RowGridColumn';
import SaveChangesModal from '../../../../../../components/SaveChangesModal';
import UserIcon from '../../../../../../components/UserIcon';
import { AdminSettingsPageDuskIds } from '../../../../../../constants/duskIds';
import { COMPANY_USER } from '../../../../../../constants/roles';
import { selectCompanyDepartments } from '../../../../../../redux/modules/company';
import { hideModal, showModal } from '../../../../../../redux/modules/modal';
import { hideTray, showTray } from '../../../../../../redux/modules/tray';
import { createMap, get } from '../../../../../../utils/arrayUtil';
import EditUserForm from '../EditUserForm';

import { activeMenuOptions, inactiveMenuOptions } from './util';

const MAX_ROLE_DISPLAY = 3;

// TODO: add ability to 'hard delete' user in UI
const UserRow = ({ user }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const departments = useSelector(selectCompanyDepartments);
  const departmentMap = useMemo(() => createMap(departments, 'departmentId'), [departments]);

  const userCompanyRole = user.roles.find(
    role => role.pivot.scopeType === 'company' && role.pivot.scopeId === parseInt(companyId, 10) && role.name !== COMPANY_USER
  );

  const userDepartmentRoles = user.roles.filter(role => role.pivot.scopeType === 'department' && departmentMap[role.pivot.scopeId]);

  const handleFormClose = formTrayProps => {
    if (formTrayProps.formIsDirty) {
      dispatch(showModal(
        () => (
          <SaveChangesModal changedInfo="user information" />
        ),
        {
          title: 'Exit without Saving?',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              onClick: () => {
                dispatch(hideModal());
                dispatch(hideTray());
              },
              dataAnalyticsId: 'cancel-user-creation'
            }
          }
        }
      ));
    } else {
      dispatch(hideModal());
      dispatch(hideTray());
    }
  };

  const showFormTray = () => {
    dispatch(showTray(() => (<EditUserForm companyId={companyId} userId={user.userId} onClose={() => dispatch(hideTray())} />), {
      placement: 'right',
      params: { companyId },
      onClose: handleFormClose,
      props: { formIsDirty: false }
    }));
  };

  return (
    <Row
      clickable={user.isActive}
      columns="repeat(17, 1fr)"
      gap={4}
      layerStyle={user.isActive ? 'row' : 'secondaryArchived'}
      onClick={user.isActive && showFormTray}
    >
      <GridItem alignItems="center" colSpan={2} display="flex" m="0 auto" marginLeft="20px">
        <UserIcon
          disabled={!user.isActive}
          firstName={user.userFirstName}
          fontSize="lg"
          iconSize="48px"
          lastName={user.userLastName}
        />
      </GridItem>
      <GridItem colSpan={5} textAlign="left">
        <RowGridColumn
          align="flex-start"
          direction="column"
          justifyContent="flex-start"
          label="Name/Email"
          maxW="100%"
          whiteSpace="nowrap"
        >
          <Text fontWeight="medium" overflow="hidden" textOverflow="ellipsis">
            {user.userFirstName}
            {' '}
            {user.userLastName}
          </Text>
          <Text fontSize="sm" lineHeight="16px" overflow="hidden" textOverflow="ellipsis">
            {user.userEmail}
          </Text>
        </RowGridColumn>
      </GridItem>

      {user.isActive && userCompanyRole && (
        <GridItem colSpan={5} textAlign="left">
          <RowGridColumn align="flex-start" flexDirection="column" label="Company Role">
            <Text>
              {userCompanyRole?.displayName || 'Unassigned'}
              {!!userDepartmentRoles.length && (
              <Text as="span" color="greyScale.600" ml={2}>{`+ ${userDepartmentRoles.length} department roles`}</Text>
              )}
            </Text>
          </RowGridColumn>
        </GridItem>
      )}

      {user.isActive && !userCompanyRole && !!userDepartmentRoles.length && (
        <GridItem colSpan={5} textAlign="left">
          <RowGridColumn align="flex-start" flexDirection="column" label="Department Roles">
            {userDepartmentRoles.slice(0, MAX_ROLE_DISPLAY).map(role => {
              const department = get(departmentMap, role.pivot.scopeId.toString());
              return department && (
              <HStack>
                <Text>{role.displayName}</Text>
                <Text color="greyScale.600">{department.departmentName}</Text>
              </HStack>
              );
            })}
            {userDepartmentRoles.length > MAX_ROLE_DISPLAY && (
            <Popover trigger="hover">
              <PopoverTrigger>
                <Text as="span" color="greyScale.600">{`+ ${userDepartmentRoles.length - MAX_ROLE_DISPLAY} more`}</Text>
              </PopoverTrigger>
              <PopoverContent maxH="300px" overflowY="auto" p={2}>
                {userDepartmentRoles.slice(MAX_ROLE_DISPLAY).map(role => {
                  const department = get(departmentMap, role.pivot.scopeId.toString());
                  return department && (
                    <HStack>
                      <Text>{role.displayName}</Text>
                      <Text color="greyScale.600">{department.departmentName}</Text>
                    </HStack>
                  );
                })}
              </PopoverContent>
            </Popover>
            )}
          </RowGridColumn>
        </GridItem>
      )}

      {user.isActive && !userCompanyRole && !userDepartmentRoles.length && (
        <GridItem colSpan={5} textAlign="left">
          <RowGridColumn align="flex-start" flexDirection="column" label="Roles">
            <Text>Unassigned</Text>
          </RowGridColumn>
        </GridItem>
      )}

      {/* TODO: add last active date */}
      {/* <GridItem colSpan={3} textAlign="left"> */}
      {/*  <RowGridColumn align="flex-start" label="Last Active"> */}
      {/*    <Text>{user.lastLogin || '9/21/2021'}</Text> */}
      {/*  </RowGridColumn> */}
      {/* </GridItem> */}

      <GridItem colSpan={5} display="flex" justifyContent="flex-end">
        <IconButtonMenu
          ariaLabel="User Options"
          dusk={AdminSettingsPageDuskIds.getUserRowSettingDropdown(user.userId)}
          iconElement={<Icon as={BsThreeDots} boxSize="20px" px={0} />}
          id={user.userId}
          menuOptions={user.isActive ? activeMenuOptions : inactiveMenuOptions}
        />
      </GridItem>
    </Row>
  );
};

UserRow.propTypes = {
  user: PropTypes.shape({
    userFirstName: PropTypes.string,
    userLastName: PropTypes.string,
    userId: PropTypes.number,
    userEmail: PropTypes.string,
    isActive: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      pivot: PropTypes.shape({
        scopeType: PropTypes.string,
        scopeId: PropTypes.number
      })
    }))
  })
};

export default UserRow;
